const clone = value => JSON.parse(JSON.stringify(value))

const SOURCE_TAB_KEY = 'source'

const objectWithout = (value, omittedKeys) => Object.entries(value || {}).reduce(
    (result, [key, item]) => (
        omittedKeys.includes(key) ? result : {...result, [key]: clone(item)}
    ),
    {}
)

const requiredTab = (definition, section) => {
    const tabs = definition
        && definition[section]
        && definition[section].tabs
    const tab = tabs && tabs[SOURCE_TAB_KEY]
    if (!tab) {
        throw new Error(`Revision does not contain the supported ${section}.tabs.source configuration.`)
    }
    return tab
}

const sourceTabName = selector => {
    if (selector && selector.key === 'system.import.tab.by_name') {
        return selector.arguments && selector.arguments.name
            ? selector.arguments.name
            : 'Configured tab'
    }
    return 'First tab'
}

const behaviorValues = configuration => {
    const value = configuration || {}
    const structure = value.structure || {}
    const identityPolicies = value.identityPolicies || {}
    const withinImport = identityPolicies.withinImport || {}
    const identityArguments = withinImport.arguments || {}
    const existingPolicyProperty = Object.keys(value).find(property => (
        property.startsWith('existing') && property.endsWith('Policy')
    ))
    const existingPolicy = existingPolicyProperty ? value[existingPolicyProperty] || {} : {}
    const existingArguments = existingPolicy.arguments || {}

    return {
        strategy: structure.strategy || null,
        identityPolicy: withinImport.key || null,
        identityField: identityArguments.field || null,
        caseSensitive: Object.prototype.hasOwnProperty.call(identityArguments, 'caseSensitive')
            ? identityArguments.caseSensitive
            : true,
        existingPolicy: existingPolicy.key || null,
        existingIdentityField: existingArguments.field || null,
        existingIdentityFields: Array.isArray(existingArguments.fields)
            ? [...existingArguments.fields]
            : [],
    }
}

const extractorHeaders = (extractor, targetFieldKey) => {
    if (extractor && extractor.key === 'system.import.extractor.column_by_header') {
        const header = extractor.arguments && typeof extractor.arguments.column === 'string'
            ? extractor.arguments.column.trim()
            : ''
        if (header) {
            return [header]
        }
    }

    if (extractor && extractor.key === 'system.import.extractor.first_present_column') {
        const headers = extractor.arguments && Array.isArray(extractor.arguments.columns)
            ? extractor.arguments.columns.map(header => (
                typeof header === 'string' ? header.trim() : ''
            ))
            : []
        if (headers.length && headers.every(Boolean)) {
            return headers
        }
    }

    throw new Error(
        `Field ${targetFieldKey} uses an extractor that this editor cannot restore.`
    )
}

const mappedRows = fields => {
    const columnIndexes = new Map()
    const columns = []
    const rows = []
    const mappingsPerColumn = new Map()

    Object.entries(fields || {}).forEach(([targetFieldKey, field], rowIndex) => {
        const extractor = field && field.extractor
        const headers = extractorHeaders(extractor, targetFieldKey)

        headers.forEach(header => {
            if (!columnIndexes.has(header)) {
                const index = columns.length
                columnIndexes.set(header, index)
                columns.push({
                    index,
                    candidateHeader: header,
                    examples: [],
                })
            }
        })

        const sourceColumnIndexes = headers.map(header => columnIndexes.get(header))
        const sourceColumnIndex = sourceColumnIndexes[0]
        const primaryHeader = headers[0]
        const mappingCount = mappingsPerColumn.get(primaryHeader) || 0
        mappingsPerColumn.set(primaryHeader, mappingCount + 1)
        rows.push({
            id: `revision-mapping-${rowIndex}`,
            sourceColumnIndex,
            sourceColumnIndexes,
            sourceHeader: primaryHeader,
            extractorKey: extractor.key,
            extractorVersion: extractor.version,
            targetFieldKey,
            pipeline: Array.isArray(field.pipeline) ? clone(field.pipeline) : [],
            additional: mappingCount > 0,
        })
    })

    return {columns, rows}
}

export const hydrateManifestDefinition = (definition, version = {}) => {
    if (!definition || typeof definition !== 'object') {
        throw new Error('Revision does not contain a manifest definition.')
    }

    const sourceTab = requiredTab(definition, 'source')
    const mappingTab = requiredTab(definition, 'mapping')
    const selector = sourceTab.selector ? clone(sourceTab.selector) : null
    const {columns, rows} = mappedRows(mappingTab.fields)
    const revisionLabel = version.revision ? `Revision ${version.revision}` : 'Selected revision'
    const fileReferenceId = `manifest-version:${version.id || version.revision || 'draft'}`
    const acceptedFileTypes = definition.source && Array.isArray(definition.source.acceptedFileTypes)
        ? [...definition.source.acceptedFileTypes]
        : ['xlsx', 'xls', 'csv']
    const target = definition.target || {}
    const planning = definition.planning || {}
    const behavior = behaviorValues(target.configuration)

    return {
        sourceState: {
            file: null,
            uploadedFile: null,
            inspection: {
                fileReferenceId,
                filename: `${revisionLabel} configuration`,
                size: 0,
                sourceType: definition.source.type || 'spreadsheet',
                fileType: acceptedFileTypes[0] || 'xlsx',
                tabs: [{
                    index: 0,
                    name: sourceTabName(selector),
                    rowCount: 0,
                    candidateHeaderRow: sourceTab.headerRow || null,
                    sampleRows: [],
                    columns,
                }],
            },
            selectedTabIndex: 0,
            headerRow: sourceTab.headerRow || null,
            dataStartRow: sourceTab.dataStartRow || null,
            selector,
            sourceType: definition.source.type || 'spreadsheet',
            acceptedFileTypes,
            sourceExtras: objectWithout(definition.source, ['type', 'acceptedFileTypes', 'tabs']),
            tabExtras: objectWithout(sourceTab, ['selector', 'headerRow', 'dataStartRow']),
            fromVersion: {
                id: version.id || null,
                revision: version.revision || null,
            },
        },
        mappingState: {
            sourceKey: `${fileReferenceId}:0`,
            rows,
            selectedRowId: rows.length ? rows[0].id : null,
            recordPipeline: Array.isArray(mappingTab.recordPipeline)
                ? clone(mappingTab.recordPipeline)
                : [],
            tabExtras: objectWithout(mappingTab, ['fields', 'recordPipeline']),
        },
        productState: {
            driver: target.driver || null,
            driverVersion: target.driverVersion || null,
            strategy: behavior.strategy,
            identityPolicy: behavior.identityPolicy,
            identityField: behavior.identityField,
            caseSensitive: behavior.caseSensitive,
            existingPolicy: behavior.existingPolicy,
            existingIdentityField: behavior.existingIdentityField,
            existingIdentityFields: behavior.existingIdentityFields,
            configuration: target.configuration ? clone(target.configuration) : null,
            requirements: [],
            requirementsLoaded: false,
            requirementsError: false,
            loadingRequirements: false,
        },
        planningState: {
            policy: planning.policy || null,
            policyVersion: planning.policyVersion || null,
            configuration: planning.configuration ? clone(planning.configuration) : {},
        },
    }
}
